import React from 'react';
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import { Download, Edit } from '@mui/icons-material';
import DocumentDetailDialog from './DocumentDetailDialog';

interface DocumentHistoryItem {
  filename: string;
  file_size: number;
  generated_at: string;
  download_url: string;
  download_url_pdf?: string | null;
  download_url_docx?: string | null;
  contract_type: string;
}

const formatBytes = (bytes: number) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const DocumentHistory: React.FC = () => {
  const [items, setItems] = useState<DocumentHistoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [detailOpen, setDetailOpen] = useState<boolean>(false);
  const [detailFilename, setDetailFilename] = useState<string | null>(null);

  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        if (isAuthenticated) {
          // Authenticated: use protected endpoint first
          try {
            const resp = await api.get('/documents/history?limit=50');
            const list = resp.data || [];
            if (Array.isArray(list) && list.length > 0) {
              setItems(list);
            } else {
              const resp2 = await fetch('/api/v1/documents/test/history?limit=50');
              if (resp2.ok) {
                const data = await resp2.json();
                setItems(data || []);
              } else {
                setItems([]);
              }
            }
          } catch (e: any) {
            // Fallback to test endpoint
            const resp = await fetch('/api/v1/documents/test/history?limit=50');
            if (resp.ok) {
              const data = await resp.json();
              setItems(data || []);
            } else {
              throw new Error('Fallback failed');
            }
          }
        } else {
          // Unauthenticated: NEVER call axios (avoid 401 -> /login redirect)
          const resp = await fetch('/api/v1/documents/test/history?limit=50');
          if (resp.ok) {
            const data = await resp.json();
            setItems(data || []);
          } else {
            setItems([]);
          }
        }
      } catch (err: any) {
        setError('Nepodařilo se načíst historii dokumentů');
      } finally {
        setLoading(false);
      }
    };
    load();
    const handler = () => load();
    window.addEventListener('document-generated', handler);
    return () => window.removeEventListener('document-generated', handler);
  }, [isAuthenticated]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (items.length === 0) {
    return <Typography color="text.secondary">Zatím nemáte žádné vygenerované dokumenty.</Typography>;
  }

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
      {items.map((item) => (
        <Card 
          key={item.filename} 
          sx={{ borderRadius: 2, cursor: 'pointer' }}
          onClick={() => {
            if (isAuthenticated) {
              setDetailFilename(item.filename);
              setDetailOpen(true);
            } else {
              // For unauthenticated users, open PDF directly to avoid protected detail endpoint
              window.open(item.download_url_pdf || item.download_url, '_blank');
            }
          }}
          role="button"
          aria-label={`Otevřít detail dokumentu ${item.filename}`}
        >
          <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {item.contract_type.toUpperCase()} — {item.filename}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {new Date(item.generated_at).toLocaleString('cs-CZ')} • {formatBytes(item.file_size)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={(e) => e.stopPropagation()}>
              {isAuthenticated && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Edit />}
                  onClick={() => {
                    setDetailFilename(item.filename);
                    setDetailOpen(true);
                  }}
                >
                  Detail / Upravit
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                onClick={() => window.open(item.download_url_pdf || item.download_url, '_blank')}
                startIcon={<Download />}
                aria-label="Stáhnout PDF"
              >
                PDF
              </Button>
              {item.download_url_docx && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => window.open(item.download_url_docx!, '_blank')}
                  startIcon={<Download />}
                  aria-label="Stáhnout DOCX"
                >
                  DOCX
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      ))}
      <DocumentDetailDialog
        open={detailOpen}
        filename={detailFilename}
        onClose={() => {
          setDetailOpen(false);
          setDetailFilename(null);
          // Reload list to reflect any new edited downloads
          try { window.dispatchEvent(new CustomEvent('document-generated')); } catch (_) {}
        }}
      />
    </Box>
  );
};

export default DocumentHistory;
